import { defineStore } from "pinia";
import { productionIdFromSearch } from "@/nav/productionScope";
import {
  fetchProduction,
  fetchProductions,
  fetchStanding,
  type ProductionChoice,
  type ProductionIdentity,
  type ProductionStanding,
} from "@/production/productionApi";
import type { HostProduction } from "@/state/globalState";

/**
 * WHICH PRODUCTION YOU ARE INSIDE — the shell's second piece of context, beside the session (D32).
 *
 * "The production is the project that is being worked on." Everything after `Productions → open` is
 * that production's: its budget, its schedule, its time, its payroll. So the shell holds it once,
 * reads it from the URL on every navigation, and publishes it on `window.PinksyGlobalState` beside
 * the user — a module never parses the URL for it, and never asks the person a second time.
 *
 * Three facts, and they arrive separately:
 *   id        from the URL, synchronously, on every routing event. This is what modules use.
 *   identity  from `GET /productions/:id`, a moment later. This is what PEOPLE use — the sidebar says
 *             the NAME of the production you are in, because a uuid is not a context anyone can read.
 *   choices   from `GET /productions`, once a session is live. The top bar's switcher, so a producer
 *             with three shows moves between them without leaving the screen they are on.
 */
interface ProductionContextState {
  id: string | null;
  identity: ProductionIdentity | null;
  /** True while the identity is in flight — the header draws a placeholder, never the uuid. */
  loadingIdentity: boolean;
  choices: ProductionChoice[];
  loadingChoices: boolean;
  /**
   * 🔴 What the caller IS on this production (D157) — null until the server has answered. The rail
   * inside a production is drawn from this and FAILS CLOSED: while it is null nothing of the
   * production's is offered, only a skeleton (`nav/navStanding.ts`). Never authority.
   */
  standing: ProductionStanding | null;
  loadingStanding: boolean;
  /** Bumped by every ask; an answer carrying an older ticket is dropped (a sign-in re-asks mid-flight). */
  standingTicket: number;
  /**
   * 🔴 Whether the person acts for a company here — `held`, `none`, or `unknown` until `GET /me/organisations` has
   * answered (boot.ts sets it). The production's record and the switcher's list are a COMPANY's questions
   * (`productions:read`): asked by a person in no company — an actor opening her own show from My work — each was a
   * permission_denied on every visit (found live as Amalia, 2026-10-02). Her standing is hers and is always asked.
   * A store used on its own is `held`: it asks what it always asked.
   */
  company: CompanyContext;
}

export type CompanyContext = "unknown" | "none" | "held";

/** How the store reaches the API. Injected whole in tests; the real calls by default. */
export interface ProductionContextDeps {
  fetchOne: (productionId: string) => Promise<ProductionIdentity | null>;
  fetchAll: () => Promise<ProductionChoice[]>;
  /** Never throws; a refusal answers "no standing", anything else null — asked again later (`fetchStanding`). */
  fetchStanding: (productionId: string) => Promise<ProductionStanding | null>;
}

const defaultDeps: ProductionContextDeps = {
  fetchOne: (id) => fetchProduction(id),
  fetchAll: () => fetchProductions(),
  fetchStanding: (id) => fetchStanding(id),
};

let deps: ProductionContextDeps = defaultDeps;

/** Test seam — `stores/__tests__/production.test.ts` swaps the API calls out. */
export function setProductionContextDeps(overrides: Partial<ProductionContextDeps> | null): void {
  deps = overrides === null ? defaultDeps : { ...defaultDeps, ...overrides };
}

export const useProductionStore = defineStore("shell-production", {
  state: (): ProductionContextState => ({
    id: null,
    identity: null,
    loadingIdentity: false,
    choices: [],
    loadingChoices: false,
    standing: null,
    loadingStanding: false,
    standingTicket: 0,
    company: "held",
  }),
  getters: {
    /** What goes on the host state — null the moment the URL stops naming a production. */
    hostProduction(state): HostProduction | null {
      return state.id === null ? null : { id: state.id, title: state.identity?.title ?? null };
    },
    /** The name to draw. Null while it is unknown; the uuid is never shown in its place. */
    title(state): string | null {
      return state.identity?.title ?? state.standing?.title ?? null;
    },
    inProduction(state): boolean {
      return state.id !== null;
    },
  },
  actions: {
    /**
     * The URL is the source of truth, so this runs on every routing event and every popstate — a
     * link, a back button and a fresh tab all arrive the same way.
     *
     * Publishing happens only when something actually changed, so a module's subscriber is not woken
     * on every navigation within one production.
     */
    syncFromLocation(search: string): void {
      const next = productionIdFromSearch(search);
      if (next !== this.id) {
        this.id = next;
        this.identity = null;
        this.loadingIdentity = false;
        // A standing is about ONE production: the previous one's answer is wrong here, not stale.
        this.standing = null;
        this.loadingStanding = false;
        this.publish();
      }
      this.askForWhatIsMissing();
    },

    /**
     * Ask for whatever is still unknown about the production we are in — and NOTHING when we are not
     * in one.
     *
     * 🔴 Both calls are lazy on purpose. The switcher's list is only ever drawn inside a production,
     * so fetching it at sign-in spent a request on every boot of every tenant app — the Passport,
     * which has no productions at all, included. And an answer we never got (signed out at the time,
     * or offline) is worth one more attempt the next time we are asked: each call guards itself with
     * its own in-flight flag, so calling this on every navigation costs nothing once it has landed.
     */
    askForWhatIsMissing(): void {
      const productionId = this.id;
      if (productionId === null) return;
      if (this.standing === null && !this.loadingStanding) void this.loadStanding(productionId);
      // 🔴 A company's questions, asked only for somebody who acts for one (`company`).
      if (this.company !== "held") return;
      if (this.identity === null && !this.loadingIdentity) void this.loadIdentity(productionId);
      if (this.choices.length === 0 && !this.loadingChoices) void this.loadChoices();
    },

    /** Whether the person acts for a company — from the organisation store, once it has answered. */
    setCompany(company: CompanyContext): void {
      if (company === this.company) return;
      this.company = company;
      if (company === "held") this.askForWhatIsMissing();
      else this.choices = [];
    },

    /**
     * The name of the production, and enough about it for the subtitle. Fetched once. A failure is not
     * fatal and is not retried on a loop: the id on the URL is still the context, and the next
     * navigation asks again.
     */
    async loadIdentity(productionId: string): Promise<void> {
      this.loadingIdentity = true;
      const identity = await deps.fetchOne(productionId);
      this.loadingIdentity = false;
      // The visitor may have left, or moved to another production, while the answer was in flight.
      if (this.id !== productionId) return;
      this.identity = identity;
      this.publish();
    },

    /**
     * What the caller is on this production (D157). Race-safe the way `loadIdentity` is: an answer for
     * a production the visitor has already left is dropped on arrival. A failure is an answer ("no
     * standing"), so the rail settles rather than showing its skeleton for ever.
     */
    async loadStanding(productionId: string): Promise<void> {
      const ticket = ++this.standingTicket;
      this.loadingStanding = true;
      const standing = await deps.fetchStanding(productionId);
      if (ticket !== this.standingTicket || this.id !== productionId) return;
      this.loadingStanding = false;
      this.standing = standing;
    },

    /**
     * A session just became live: whatever was asked before it — refused as nobody, or still in flight
     * without a token — is not this person's answer. Forget it and ask again.
     */
    reaskStanding(): void {
      this.standingTicket += 1;
      this.standing = null;
      this.loadingStanding = false;
      this.askForWhatIsMissing();
    },

    /**
     * What the top bar's switcher offers. A failure leaves the list empty, which the switcher reads
     * as "nothing to switch to" and draws as a plain label rather than a menu that does nothing.
     */
    async loadChoices(): Promise<void> {
      if (this.loadingChoices) return;
      this.loadingChoices = true;
      const choices = await deps.fetchAll();
      this.loadingChoices = false;
      this.choices = choices;
    },

    /** A sign-out ends the context with the session — the next person starts outside every production. */
    clear(): void {
      this.choices = [];
      this.loadingChoices = false;
      // The next person at this browser is somebody else on every production.
      this.standingTicket += 1;
      this.standing = null;
      this.loadingStanding = false;
      if (this.id === null && this.identity === null) return;
      this.id = null;
      this.identity = null;
      this.loadingIdentity = false;
      this.publish();
    },

    publish(): void {
      window.PinksyGlobalState?.updateState({ production: this.hostProduction });
    },
  },
});
